/* The marketing site's FAQ copy: the pages render it, and the same questions go out as
   FAQPage JSON-LD, so the two cannot drift apart. */
export interface MarketingFaq {
  q: string;
  a: string;
}

export const LANDING_FAQS: MarketingFaq[] = [
  {
    q: "Can I try the platform before talking to anyone?",
    a: "Yes. The Experience page has live interactive demos with sample data: run a med pass, walk a move-in, work the CRM pipeline and collect invoices, all in your browser with no account. When you want the full picture, book a 30-minute demo.",
  },
  {
    q: "How long does it take to get set up?",
    a: "Most teams are running the same day. A guided wizard creates your first community, then you invite staff and add residents immediately. Modules like billing, therapy and pharmacy can be switched on later without any migration.",
  },
  {
    q: "Does it handle medications and the MAR?",
    a: "Yes, a full eMAR: physician orders, scheduled med passes with tap-to-administer, holds and refusals with documentation, automatic late/missed tracking, plus pharmacy receiving, supply run-out forecasting and refill workflows.",
  },
  {
    q: "How does billing and getting paid work?",
    a: "Charges roll into monthly invoices automatically. Families pay online by ACH or card, autopay runs on its own, and paper checks and ACH are recorded too. Rent roll, AR aging, collections, expenses, P&L and trust fund reports are built in.",
  },
  {
    q: "Do you support Medicaid and managed care?",
    a: "Yes. Per-resident authorization tracking with payers, modifiers and levels of care, an Expiring Auths report, and Medicaid-specific move-in pathways such as STAR+PLUS.",
  },
  {
    q: "Can families, physicians and partners get access?",
    a: "Each gets their own portal. Families see invoices and can pay online and e-sign documents; physicians review and sign orders and plans of care remotely; home health and hospice partners chart against only the residents they serve.",
  },
  {
    q: "How is my data protected? Are you HIPAA compliant?",
    a: "PEOPLE AI Workforce is HIPAA compliant. Granular role-based access, strict per-organization data isolation, audit trails, PIN sign-in locked to your facility network, automatic idle sign-out, and hourly encrypted backups to independent off-site storage. Each organization only ever sees its own communities and residents.",
  },
  {
    q: "Does it replace our paper admission packets?",
    a: "Yes. Admission packets are e-signed (ESIGN/UETA-aligned) in person or by secure remote link, intake questions are asked once and flow everywhere, and signed documents file themselves into the resident's chart.",
  },
  {
    q: "Can I run multiple communities on one account?",
    a: "The platform is multi-facility from the ground up: an All Facilities view for administrators, per-facility scoping for staff, and consolidated reporting across every location.",
  },
  {
    q: "What about our staff: hiring, training, shift change?",
    a: "A hiring pipeline with job postings and offer templates, HR document management, a training system that issues certificates, staff scheduling, and a tablet handoff board that runs a structured shift-change ceremony.",
  },
  {
    q: "Can we send faxes and texts from the platform?",
    a: "Yes. Every facility gets a fax line and SMS number. Send charts, orders and referrals by fax straight from the record; text families and applicants from a shared inbox with read tracking.",
  },
  {
    q: "Can we get our data out if we ever leave?",
    a: "Always. Residents, financials, reports and documents export to CSV, Excel and PDF at any time, from inside the product. Your records are yours. There's no lock-in and no exit fee.",
  },
  {
    q: "What does it cost?",
    a: "Plans are per-community with modules you can switch on as you grow. See the Pricing page for details, or book a demo and we'll help you pick the right fit.",
  },
];

export const PRICING_FAQS: MarketingFaq[] = [
  {
    q: "Why don't you list prices?",
    a: "Because what varies is which AI employees you add, not what you look like on paper. PEOPLE Core is free, and each AI employee you switch on is its own subscription, priced per community, so the figure follows the ones you choose. We would rather quote yours than post one that is wrong for you. Book a demo and you leave the call with the number for your operation.",
  },
  {
    q: "What is the price built from?",
    a: "One unit: each AI employee you switch on, per community. PEOPLE Core costs nothing. There is no per-bed, per-resident or per-seat math. Filling a room, hiring a caregiver or giving an administrator an account does not move the price.",
  },
  {
    q: "What comes free with PEOPLE Core?",
    a: "Residents, care plans and eMAR, billing, compliance, documents and your staff, plus secure role-based access, multi-facility support, per-organization data isolation, encrypted off-site backups and data export to CSV, Excel and PDF. Security is never an upsell.",
  },
  {
    q: "Can I add or drop an AI employee later?",
    a: "Yes. Add an AI employee the month you want it and drop it the month you don't. Nothing waits for a renewal date. Your data and configuration carry over; a change switches capability on or off, it does not restart you.",
  },
  {
    q: "Are Medicaid features included?",
    a: "Yes. Medicaid/MCO authorization tracking and rate management come with PEOPLE Core for organizations that accept Medicaid.",
  },
  {
    q: "Is there a long-term contract?",
    a: "No. It is month to month: no annual commitment to start, no setup or implementation fee, and no exit fee if you go. Residents, financials, reports and documents export to CSV, Excel and PDF from inside the product at any time. There is no lock-in on your own records.",
  },
];

export const SECURITY_FAQS: MarketingFaq[] = [
  {
    q: "Is PEOPLE AI Workforce HIPAA compliant?",
    a: "Yes. PEOPLE AI Workforce is HIPAA compliant: role-based access control, per-tenant isolation, audit trails, encrypted backups and secure storage protect PHI at every layer. PEOPLE AI Workforce operates as a business associate, and every order comes with a Business Associate Agreement, accepted at checkout before any resident information is entered. Operators remain responsible for their own policies and workforce training as part of their overall compliance program.",
  },
  {
    q: "How is resident data separated between organizations?",
    a: "The platform enforces per-tenant data isolation. Every PHI record is tied to a facility, and a facility belongs to one organization, so users only ever see data within their own organization's communities.",
  },
  {
    q: "How are documents stored and shared?",
    a: "Uploaded documents live in object storage and are accessed through short-lived presigned URLs rather than public links, so files aren't exposed to anyone without an authenticated, scoped request.",
  },
  {
    q: "What happens when a clinical note is signed?",
    a: "Signing verifies the signer's account password, applies an e-signature block and locks the note. Signed notes are immutable; any later change requires an explicit unlock, edit and re-sign, all of which are captured in the audit trail.",
  },
];
